import { FC } from 'react'

import { styled } from 'styled-components'

import {
  durationNormal,
  easingStandard,
  fontSizeLabel,
  fontWeightMedium,
  radiusInput,
  shadowSm,
} from 'core/styles'

import { segmentRadius } from './common'

export type SegmentedControlOption = {
  label: string
  value: string
  disabled?: boolean
}

type SegmentedControlProps = {
  className?: string
  options: Array<SegmentedControlOption>
  value: string
  onChange: (value: string) => void
}

/**
 * A compact tab-like switcher: a quiet track with one raised active segment.
 */
const SegmentedControl: FC<SegmentedControlProps> = ({
  className,
  options,
  value,
  onChange,
}) => (
  <Track className={className} role='tablist'>
    {options.map((option) => (
      <Segment
        $isActive={option.value === value}
        aria-selected={option.value === value}
        disabled={option.disabled}
        key={option.value}
        onClick={() => {
          if (option.value !== value) onChange(option.value)
        }}
        role='tab'
        type='button'
      >
        {option.label}
      </Segment>
    ))}
  </Track>
)

export default SegmentedControl

const Track = styled.div`
  display: inline-flex;
  gap: 2px;
  align-items: center;
  border-radius: ${radiusInput};
  background-color: ${(p) => p.theme.surfaceDim};
  padding: 2px;
`

const Segment = styled.button<{ $isActive: boolean }>`
  transition: background-color ${durationNormal} ${easingStandard};
  border: none;
  border-radius: ${segmentRadius};
  cursor: pointer;
  padding: 4px 12px;
  font-size: ${fontSizeLabel};

  ${(p) =>
    p.$isActive
      ? `
      background-color: ${p.theme.background};
      box-shadow: ${shadowSm};
      color: ${p.theme.text};
      font-weight: ${fontWeightMedium};
      `
      : `
      background-color: transparent;
      color: ${p.theme.labelText};
      `}

  &:disabled {
    cursor: default;
    color: ${(p) => p.theme.disabledText};
  }
`
